import { z } from 'zod';

import { OptionEntity } from 'types/OptionEntity';

export const accreditationExtraDataFieldTypeSchema = z.enum(['text', 'dropdown', 'checkbox', 'file']);

export type AccreditationExtraDataFieldType = z.infer<typeof accreditationExtraDataFieldTypeSchema>;

const accreditationFieldOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const rawAccreditationExtraDataFieldSchema = z.object({
  field_key: z.string(),
  field_type: accreditationExtraDataFieldTypeSchema,
  label: z.string(),
  options: z.array(accreditationFieldOptionSchema).nullable().optional(),
  is_required: z.boolean(),
  value: z.string().nullable().optional(),
  attachment_id: z.number().nullable().optional(),
  read_only: z.boolean().optional(),
});

const mapDropdownOptions = (
  options: Array<z.infer<typeof accreditationFieldOptionSchema>> | null | undefined,
): OptionEntity<string, string>[] => {
  return (options ?? []).map((option) => ({
    key: option.value,
    value: option.label,
  }));
};

export const accreditationExtraDataFieldSchema = rawAccreditationExtraDataFieldSchema.transform((raw) => {
  const commonFields = {
    fieldName: raw.field_key,
    label: raw.label,
    isRequired: raw.is_required,
    sortOrder: 0,
    readOnly: raw.read_only ?? false,
    attachmentId: raw.attachment_id ?? null,
  };

  switch (raw.field_type) {
    case 'text':
      return {
        ...commonFields,
        fieldType: 'text' as const,
        value: raw.value ?? null,
      };
    case 'dropdown':
      return {
        ...commonFields,
        fieldType: 'dropdown' as const,
        options: mapDropdownOptions(raw.options),
        value: raw.value ?? null,
      };
    case 'checkbox':
      return {
        ...commonFields,
        fieldType: 'checkbox' as const,
        value: raw.value?.toLowerCase() === 'true',
      };
    case 'file':
      return {
        ...commonFields,
        fieldType: 'file' as const,
        value: raw.attachment_id ?? null,
      };
  }
});

export type AccreditationExtraDataFieldEntity = z.infer<typeof accreditationExtraDataFieldSchema>;

export const accreditationExtraDataFieldsSchema = z.array(accreditationExtraDataFieldSchema);
